import type { Actor, ActorType, Container, ContainerType } from "@shared/types/supabase.types";
import type { ActorByType, Member, MemberActorType, MemberByType } from "./containers.types";

export function isContainerType(containerType: ContainerType) {
  return (container: Container | null): boolean =>
    !!container &&
    container.container_type === containerType &&
    (containerType !== "project" && containerType !== "workspace"
      ? true
      : typeof container.slug === "string" && container.slug.trim().length > 0);
}

export function isActorType<T extends ActorType>(actorType: T) {
  return (actor: Actor | null): actor is ActorByType<T> =>
    !!actor && actor.actor_type === actorType;
}

export function isMemberType<T extends MemberActorType>(actorType: T) {
  return (member: Member | null): member is MemberByType<T> =>
    !!member && member.actor_type === actorType;
}

/**
 * The display name for a container member — human = "first last" || email,
 * invited = email || domain, AI = persona display name. Returns null when
 * nothing resolves (callers decide the fallback: the avatar group shows
 * "Invited user", the message-room title (CR-67) drops the participant). Keep
 * the SQL twin in get_message_room_member_names.sql in sync.
 */
export function resolveMemberDisplayName(member: Member): string | null {
  if (member.actor_type === "ai") {
    return member.ai_persona_display_name || null;
  }
  if (member.actor_type === "invited_user") {
    return member.invite_email || member.invite_domain || null;
  }
  return (
    `${member.profile_first_name ?? ""} ${member.profile_last_name ?? ""}`.trim() ||
    member.profile_email ||
    null
  );
}
